import { useLazyQuery } from '@apollo/client'
import iconPen from 'assets/images/icon-pen-bold.svg'
import iconTimes from 'assets/images/icon-times.svg'
import iconReset from 'assets/images/reset-bold.svg'
import Loading from 'components/loading'
import ButtonBase from 'components/partials/button'
import InputBase from 'components/partials/inputBase'
import { notify } from 'components/toastify'
import { STATUS } from 'constants/messages'
import moment from 'moment'
import { IDataForGenerateJob } from 'pages/email-system-management/received-email-text-history/interface'
import { GENERATE_JOB_MANUALLY } from 'queries/adminPortal'
import React, { useEffect, useState } from 'react'

interface Props {
  data: IDataForGenerateJob | null
  handleCloseDialog?: (val: boolean) => void
  handleBackBtn?: (applicationNumber: string, carrierBondNumber: string, step: number) => void
  refetchEmailTable?: () => void
}

interface IRequiredMetadataUpdate {
  expiryDate?: string | null
  bondEmail?: string | null
  contactPhone?: string | null
  paymentLink?: string | null
}

const ConfirmGenerateJobDialog: React.FC<Props> = ({
  handleCloseDialog = () => null,
  data,
  handleBackBtn = () => null,
  refetchEmailTable = () => null,
}) => {
  const [isBlockGenerate, setIsBlockGenerate] = useState<boolean>(false)
  const [dataMissing, setDataMissing] = useState<string[]>([])
  const [updatedMetadata, setUpdatedMetadata] = useState<IRequiredMetadataUpdate>()
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [expiryDate, setExpiryDate] = useState<Date>(new Date())

  const classInfoKey = 'text-neutral-3 text-body2 font-semibold leading-6 min-w-[160px]'
  const classInfoKeyEditMode = `${classInfoKey}  my-2`
  const classInfoVal = 'text-neutral-3 text-body1 min-w-[460px] break-words'

  const [generateJobManually, { loading: loadingGenerateJobManually }] =
    useLazyQuery(GENERATE_JOB_MANUALLY)

  const onSetDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpiryDate(new Date(e?.target?.value ?? e))
    onChangeMetadata('expiryDate', e)
  }

  const handleGenerateBtn = async () => {
    if (isBlockGenerate) {
      notify(`Missing data to generate job(s): ${dataMissing.join(', ')}`, STATUS.ERROR)
      return
    }

    const response = await generateJobManually({
      variables: {
        messageId: data?.messageId,
        updatedMetadata,
      },
      fetchPolicy: 'no-cache',
    })

    const dataResponse = response.data?.generateJobManually

    if (dataResponse && dataResponse.status) {
      handleCloseDialog(false)
      notify(`Generate Job successfully for ${data?.applicationNumber}`, STATUS.SUCCESS)
      refetchEmailTable()
    } else if (dataResponse && !dataResponse.status && dataResponse.failedReason) {
      notify(dataResponse.failedReason, STATUS.ERROR)
    } else {
      notify(`Something wrong`, STATUS.ERROR)
    }
  }

  const getKeysWithNullOrEmptyValues = <T extends Record<string, any>>(obj: T): (keyof T)[] =>
    Object.keys(obj).filter(
      (key) =>
        obj[key] === null ||
        obj[key].trim() === '' ||
        obj[key].trim().toLowerCase() === 'invalid date',
    ) as (keyof T)[]

  const verifyRequiredData = () => {
    if (
      updatedMetadata &&
      (!updatedMetadata.paymentLink ||
        !updatedMetadata.expiryDate ||
        !updatedMetadata.bondEmail ||
        !updatedMetadata.contactPhone)
    ) {
      const listDataMissing = getKeysWithNullOrEmptyValues(updatedMetadata)
      setDataMissing(listDataMissing)
      setIsBlockGenerate(true)
    } else {
      setIsBlockGenerate(false)
    }
  }

  useEffect(() => {
    if (data) {
      const updatedData = { ...updatedMetadata }
      updatedData.bondEmail = data.bondEmail
      updatedData.contactPhone = data.contactPhone
      updatedData.expiryDate = data.expiryDate
      updatedData.paymentLink = data.paymentLink
      setUpdatedMetadata(updatedData)
    }
  }, [data])

  useEffect(() => {
    verifyRequiredData()
  }, [updatedMetadata])

  const onEditButton = () => {
    setIsEditMode(!isEditMode)
  }

  const onChangeMetadata = (
    key: keyof IRequiredMetadataUpdate,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const temp = { ...updatedMetadata }
    let value = e?.target?.value ?? e

    if (key === 'expiryDate') {
      const data = e?.target?.value ?? e
      value = moment(data).format('MM/DD/YYYY')
    }

    temp[key] = value

    setUpdatedMetadata(temp)
  }

  return (
    <div className="w-full p-1 pr-0">
      <div className="flex flex-col mb-2">
        <div className="mb-3">
          <p className="text-headline5">Generate Job</p>
          <div className="absolute right-4 top-4">
            <img
              src={iconTimes}
              alt=""
              className="cursor-pointer"
              onClick={() => handleCloseDialog(false)}
            />
          </div>
        </div>
        <div className="flex justify-between">
          <p className="">
            <strong>Step 2:</strong> Review and confirm generate job(s)
          </p>
          <ButtonBase
            className="justify-around w-[89px] h-[40px] px-3 border-2 border-neutral-6 text-neutral-4 bg-neutral-8 hover:bg-neutral-7 hover:text-neutral-3"
            onClick={onEditButton}
          >
            {!isEditMode ? (
              <div className="flex justify-between">
                <img src={iconPen} alt="iconPen" width={20} height={20} />
                <p>Edit</p>
              </div>
            ) : (
              <div className="flex justify-between">
                <img src={iconReset} alt="iconReset" width={20} height={20} />
                <p>Reset</p>
              </div>
            )}
          </ButtonBase>
        </div>
      </div>

      <hr className="mt-2 mb-5 mr-1 border-neutral-5" />

      <div className="flex-row h-[300px] overflow-y-scroll">
        <div className="flex mb-4">
          <div className="w-2/6">
            <p className={classInfoKey}>Application Number</p>
          </div>
          <div className="w-4/6">
            <p className={classInfoVal}>{data?.applicationNumber}</p>
          </div>
        </div>
        <div className="flex mb-4">
          <div className="w-2/6">
            <p className={classInfoKey}>Carrier Bond Number</p>
          </div>
          <div className="w-4/6">
            <p className={classInfoVal}>{data?.carrierBondNumber}</p>
          </div>
        </div>
        <div className="flex mb-4">
          <div className="w-2/6">
            <p className={classInfoKey}>Bond Description</p>
          </div>
          <div className="w-4/6">
            <p className={classInfoVal}>{data?.bondDescription}</p>
          </div>
        </div>
        <div className="flex mb-4">
          <div className="w-2/6">
            <p className={isEditMode ? classInfoKeyEditMode : classInfoKey}>
              Bond Email <span style={{ color: 'red' }}>*</span>
            </p>
          </div>
          <div className="w-4/6">
            {isEditMode ? (
              <InputBase
                placeholder="Bond Email address"
                type="email"
                className="w-[95%] h-[90%] font-normal p-2 pl-3"
                defaultValue={data?.bondEmail ?? ''}
                handleChange={(e) => onChangeMetadata('bondEmail', e)}
              />
            ) : (
              <p className={classInfoVal}>{data?.bondEmail}</p>
            )}
          </div>
        </div>
        <div className="flex mb-4">
          <div className="w-2/6">
            <p className={classInfoKey}>Principal Name</p>
          </div>
          <div className="w-4/6">
            <p className={classInfoVal}>{data?.principalName}</p>
          </div>
        </div>
        <div className="flex mb-4">
          <div className="w-2/6">
            <p className={classInfoKey}>Contact Name</p>
          </div>
          <div className="w-4/6">
            <p className={classInfoVal}>{data?.contactName}</p>
          </div>
        </div>
        <div className="flex mb-4">
          <div className="w-2/6">
            <p className={isEditMode ? classInfoKeyEditMode : classInfoKey}>
              Contact Phone <span style={{ color: 'red' }}>*</span>
            </p>
          </div>
          <div className="w-4/6">
            {isEditMode ? (
              <InputBase
                placeholder="Contact Phone"
                type="text"
                className="w-[95%] h-[90%] font-normal  py-2 pl-3"
                defaultValue={data?.contactPhone ?? ''}
                handleChange={(e) => onChangeMetadata('contactPhone', e)}
              />
            ) : (
              <p className={classInfoVal}>{data?.contactPhone}</p>
            )}
          </div>
        </div>
        <div className="flex mb-4">
          <div className="w-2/6">
            <p className={classInfoKey}>Agency Name</p>
          </div>
          <div className="w-4/6">
            <p className={classInfoVal}>{data?.agencyName}</p>
          </div>
        </div>
        <div className="flex mb-4">
          <div className="w-2/6">
            <p className={classInfoKey}>Agency Email</p>
          </div>
          <div className="w-4/6">
            <p className={classInfoVal}>{data?.agencyEmail}</p>
          </div>
        </div>
        <div className="flex mb-4">
          <div className="w-2/6">
            <p className={classInfoKey}>Subject</p>
          </div>
          <div className="w-4/6">
            <p className={classInfoVal}>{data?.subject}</p>
          </div>
        </div>
        <div className="flex mb-4">
          <div className="w-2/6">
            <p className={isEditMode ? classInfoKeyEditMode : classInfoKey}>
              Expiry Date <span style={{ color: 'red' }}>*</span>
            </p>
          </div>
          <div className="w-4/6">
            {isEditMode ? (
              <InputBase
                placeholder="Expiry Date"
                type="date"
                pattern=""
                className="w-[40%] h-[90%] font-normal py-2 pl-3"
                handleChange={(e) => onSetDate(e)}
                value={expiryDate.toLocaleDateString('en-CA')}
              />
            ) : (
              <p className={classInfoVal}>{data?.expiryDate}</p>
            )}
          </div>
        </div>
        <div className="flex mb-4">
          <div className="w-2/6">
            <p className={classInfoKey}>Renewal Effective Date</p>
          </div>
          <div className="w-4/6">
            <p className={classInfoVal}>{data?.renewalEffectiveDate}</p>
          </div>
        </div>
        <div className="flex mb-4">
          <div className="w-2/6">
            <p className={classInfoKey}>Transaction Premium</p>
          </div>
          <div className="w-4/6">
            <p className={classInfoVal}>{data?.transactionPremium}</p>
          </div>
        </div>
        <div className="flex mb-4">
          <div className="w-2/6">
            <p className={isEditMode ? classInfoKeyEditMode : classInfoKey}>
              Payment Link <span style={{ color: 'red' }}>*</span>
            </p>
          </div>
          <div className="w-4/6">
            {isEditMode ? (
              <InputBase
                placeholder="Payment Link URL"
                type="text"
                className="w-[95%] h-[90%] font-normal p-3 pl-3"
                defaultValue={data?.paymentLink ?? ''}
                handleChange={(e) => onChangeMetadata('paymentLink', e)}
              />
            ) : (
              <p className={classInfoVal}>{data?.paymentLink}</p>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-between">
        <ButtonBase
          type="submit"
          className="w-[150px] my-4 mt-6 justify-around border-2 border-primary-1 text-primary-1 bg-white-50 hover:bg-primary-shade4"
          onClick={() => {
            handleBackBtn(data?.applicationNumber || '', data?.carrierBondNumber || '', 1)
          }}
          bgDisabled="bg-primary-shade3"
        >
          Back
        </ButtonBase>
        <ButtonBase
          type="submit"
          className="w-[150px] my-4 mt-6 justify-around border-2 border-primary-1"
          onClick={handleGenerateBtn}
        >
          Generate
        </ButtonBase>
      </div>

      {loadingGenerateJobManually && (
        <Loading
          className="absolute inset-0 flex items-center justify-center bg-gray-500 bg-opacity-70 z-50 rounded-[16px]"
          height={30}
          width={30}
        />
      )}
    </div>
  )
}

export default ConfirmGenerateJobDialog
